import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  Select,
  MenuItem,
  Checkbox,
  RadioGroup,
  Radio,
  Button,
  Box,
  Grid,
  Typography
} from '@mui/material';

export default function DynamicFormEditor({ formDef, onSubmit }) {
  // Initialize form values
  const [values, setValues] = useState(() => {
    const init = {};
    formDef.groups.forEach(group => {
      group.fields.forEach(field => {
        if (field.type === 'checkbox') init[field.id] = false;
        else init[field.id] = '';
      });
    });
    return init;
  });

  const handleChange = (fieldId, value) => {
    setValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (onSubmit) onSubmit(values);
    else console.log('Form submitted:', values);
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader title={formDef.name} subheader={formDef.description} />
      <CardContent>
        <form onSubmit={handleSubmit}>
          {formDef.groups.map(group => (
            <Box key={group.id} sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {group.name}
              </Typography>
              {group.description && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {group.description}
                </Typography>
              )}
              <Grid container spacing={2}>
                {group.fields.map(field => {
                  const value = values[field.id];
                  switch (field.type) {
                    case 'text':
                      return (
                        <Grid item xs={12} key={field.id}>
                          <TextField
                            fullWidth
                            required={field.required}
                            label={field.name}
                            value={value}
                            onChange={e => handleChange(field.id, e.target.value)}
                          />
                        </Grid>
                      );
                    case 'textarea':
                      return (
                        <Grid item xs={12} key={field.id}>
                          <TextField
                            fullWidth
                            required={field.required}
                            multiline
                            minRows={3}
                            label={field.name}
                            value={value}
                            onChange={e => handleChange(field.id, e.target.value)}
                          />
                        </Grid>
                      );
                    case 'number':
                      return (
                        <Grid item xs={12} key={field.id}>
                          <TextField
                            fullWidth
                            required={field.required}
                            type="number"
                            label={field.name}
                            value={value}
                            onChange={e => handleChange(field.id, e.target.value)}
                          />
                        </Grid>
                      );
                    case 'date':
                      return (
                        <Grid item xs={12} sm={6} key={field.id}>
                          <TextField
                            fullWidth
                            required={field.required}
                            type="date"
                            label={field.name}
                            InputLabelProps={{ shrink: true }}
                            value={value}
                            onChange={e => handleChange(field.id, e.target.value)}
                          />
                        </Grid>
                      );
                    case 'datetime':
                      return (
                        <Grid item xs={12} sm={6} key={field.id}>
                          <TextField
                            fullWidth
                            required={field.required}
                            type="datetime-local"
                            label={field.name}
                            InputLabelProps={{ shrink: true }}
                            value={value}
                            onChange={e => handleChange(field.id, e.target.value)}
                          />
                        </Grid>
                      );
                    case 'select': {
                      const opts = field.options ? field.options.split(',') : [];
                      return (
                        <Grid item xs={12} sm={6} key={field.id}>
                          <FormControl fullWidth required={field.required}>
                            <FormLabel>{field.name}</FormLabel>
                            <Select
                              value={value}
                              onChange={e => handleChange(field.id, e.target.value)}
                            >
                              {opts.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      );
                    }
                    case 'radio': {
                      const opts = field.options ? field.options.split(',') : [];
                      return (
                        <Grid item xs={12} key={field.id}>
                          <FormControl component="fieldset" required={field.required}>
                            <FormLabel component="legend">{field.name}</FormLabel>
                            <RadioGroup
                              value={value}
                              onChange={e => handleChange(field.id, e.target.value)}
                            >
                              {opts.map(opt => (
                                <FormControlLabel
                                  key={opt}
                                  value={opt}
                                  control={<Radio />}
                                  label={opt}
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                      );
                    }
                    case 'checkbox':
                      return (
                        <Grid item xs={12} key={field.id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!value}
                                onChange={e => handleChange(field.id, e.target.checked)}
                              />
                            }
                            label={field.name}
                          />
                        </Grid>
                      );
                    case 'none':
                      return (
                        <Grid item xs={12} key={field.id}>
                          <Typography variant="body2" color="text.secondary">
                            {field.information}
                          </Typography>
                        </Grid>
                      );
                    default:
                      return null;
                  }
                })}
              </Grid>
            </Box>
          ))}
          <Box sx={{ mt: 2 }}>
            <Button type="submit" variant="contained">
              Submit
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
}